import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Check,
  Sparkles,
  Zap,
  Crown,
  Rocket,
  Lock,
  Unlock,
  Loader2,
} from 'lucide-react';
import { AI_TUTOR_PLANS } from '../../services/aiTutorSubscriptionService';
import paymentModeService from '../../services/paymentModeService';
import { paymentManagementService } from '../../firebase/paymentManagement';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const AITutorSubscriptionModal = ({ isOpen, onClose, onSubscribe, currentPlan = 'free' }) => {
  const { currentUser, userProfile } = useRealtime();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  const plans = Object.values(AI_TUTOR_PLANS).filter(plan => plan.id !== 'free');

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'basic':
        return <Zap className="w-6 h-6" />;
      case 'pro':
        return <Sparkles className="w-6 h-6" />;
      case 'advanced':
        return <Crown className="w-6 h-6" />;
      default:
        return <Lock className="w-6 h-6" />;
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (plan) => {
    if (!currentUser) {
      toast.error('Please login to subscribe');
      return;
    }

    setIsProcessing(true);
    setSelectedPlan(plan.id);

    try {
      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        // Load Razorpay script
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          toast.error('Failed to load payment gateway. Please try again.');
          setIsProcessing(false);
          setSelectedPlan(null);
          return;
        }
      }

      // Get Razorpay config
      const razorpayConfig = paymentModeService.getRazorpayConfig();
      
      if (!razorpayConfig || !razorpayConfig.keyId) {
        toast.error('Payment system not initialized. Please contact support.');
        setIsProcessing(false);
        setSelectedPlan(null);
        return;
      }

      // Convert amount to paise (add 18% GST)
      const basePrice = billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly;
      const priceWithGST = Math.round(basePrice * 1.18);
      const amountInPaise = Math.round(priceWithGST * 100);

      // Initialize Razorpay Checkout
      const options = {
        key: razorpayConfig.keyId,
        amount: amountInPaise,
        currency: 'INR',
        name: 'Mahstry Private Limited',
        description: `AI Tutor ${plan.name} Plan - ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`,
        image: '/Mahstry.jpeg',
        prefill: {
          name: userProfile?.name || currentUser.displayName || '',
          email: currentUser.email || '',
          contact: userProfile?.phone || ''
        },
        theme: {
          color: '#7C3AED'
        },
        handler: async function (response) {
          try {
            // Create transaction data
            const transactionData = {
              orderId: response.razorpay_order_id || `order_${Date.now()}`,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature || null,
              amount: priceWithGST,
              baseAmount: basePrice,
              gstAmount: priceWithGST - basePrice,
              currency: 'INR',
              status: 'success',
              timestamp: new Date().toISOString(),
              userId: currentUser.uid,
              paymentType: 'ai_tutor_subscription',
              planId: plan.id,
              planName: plan.name,
              billingCycle: billingCycle,
            };

            // Save transaction to Firebase
            const saveResult = await paymentManagementService.savePaymentTransaction(transactionData);
            
            if (!saveResult.success) {
              throw new Error(saveResult.error || 'Failed to save transaction');
            }

            // Call the onSubscribe callback
            await onSubscribe(plan.id, {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });

            toast.success(`Successfully subscribed to ${plan.name} plan!`);
            setIsProcessing(false);
            setSelectedPlan(null);
            onClose();
          } catch (error) {
            console.error('Payment processing error:', error);
            toast.error('Payment completed but there was an error. Please contact support.');
            setIsProcessing(false);
            setSelectedPlan(null);
          }
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            setSelectedPlan(null);
          },
        },
        notes: {
          userId: currentUser.uid,
          planId: plan.id,
          planName: plan.name,
          paymentType: 'ai_tutor_subscription',
          billingCycle: billingCycle,
        },
      };

      const razorpay = new window.Razorpay(options);
      
      // Add error handler
      razorpay.on('payment.failed', function (response) {
        toast.error(response.error?.description || 'Payment failed. Please try again.');
        setIsProcessing(false);
        setSelectedPlan(null);
      });
      
      razorpay.open();
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to process subscription. Please try again.');
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 p-6 rounded-t-lg z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Rocket className="w-7 h-7 mr-3" />
                  Unlock MahStry AI Tutor
                </h2>
                <p className="text-purple-100 mt-1">
                  {currentPlan === 'free' 
                    ? 'Upgrade from Free plan to get more AI doubts daily' 
                    : 'Choose a plan that fits your learning needs'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Current Plan Info for Free Users */}
          {currentPlan === 'free' && (
            <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-1">
                    You're on the Free Plan
                  </h3>
                  <p className="text-sm text-yellow-800 dark:text-yellow-400">
                    You get <strong>1 AI doubt per day</strong>. Upgrade to unlock more doubts and get faster, detailed solutions!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Billing Cycle Toggle */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center space-x-4">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  billingCycle === 'yearly' ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                Yearly
              </span>
              {billingCycle === 'yearly' && (
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold rounded-full">
                  Save 17%
                </span>
              )}
            </div>
          </div>

          {/* Plans */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const isCurrentPlan = currentPlan === plan.id;
                const isPro = plan.popular;
                const price = billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly;
                const monthlyPrice = billingCycle === 'yearly' ? Math.round(plan.priceYearly / 12) : plan.priceMonthly;

                return (
                  <motion.div
                    key={plan.id}
                    whileHover={{ scale: 1.02 }}
                    className={`relative rounded-xl border-2 p-6 transition-all ${
                      isPro
                        ? 'border-purple-500 shadow-lg shadow-purple-500/20'
                        : 'border-gray-200 dark:border-gray-700'
                    } ${
                      isCurrentPlan
                        ? 'bg-green-50 dark:bg-green-900/20'
                        : 'bg-white dark:bg-gray-800'
                    }`}
                  >
                    {/* Popular Badge */}
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                          MOST POPULAR
                        </span>
                      </div>
                    )}

                    {/* Current Plan Badge */}
                    {isCurrentPlan && (
                      <div className="absolute -top-3 right-4">
                        <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center">
                          <Check className="w-3 h-3 mr-1" />
                          CURRENT
                        </span>
                      </div>
                    )}

                    {/* Plan Icon */}
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${
                        plan.id === 'basic'
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                          : plan.id === 'pro'
                          ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                      }`}
                    >
                      {getPlanIcon(plan.id)}
                    </div>

                    {/* Plan Name */}
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {plan.name}
                    </h3>

                    {/* Price */}
                    <div className="mb-4">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">
                          ₹{Math.round(price * 1.18)}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400 ml-1">
                          /{billingCycle === 'yearly' ? 'year' : 'month'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Base: ₹{price} + GST (18%)
                      </p>
                      {billingCycle === 'yearly' && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          ₹{Math.round((price * 1.18) / 12)}/month
                        </p>
                      )}
                    </div>

                    {/* Doubts & Images Per Day */}
                    <div className="mb-6 space-y-2">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Daily AI Doubts
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {plan.doubtsPerDay}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Image Uploads/Day
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {plan.imagesPerDay}
                        </p>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Subscribe Button */}
                    <button
                      onClick={() => handleSubscribe(plan)}
                      disabled={isCurrentPlan || isProcessing}
                      className={`w-full py-3 rounded-lg font-semibold transition-all ${
                        isCurrentPlan
                          ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                          : isPro
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg'
                          : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                      } ${
                        isProcessing && selectedPlan === plan.id
                          ? 'opacity-50 cursor-wait'
                          : ''
                      }`}
                    >
                      {isProcessing && selectedPlan === plan.id ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Processing...
                        </span>
                      ) : isCurrentPlan ? (
                        'Current Plan'
                      ) : (
                        `Subscribe to ${plan.name}`
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </div>

            {/* Note */}
            <div className="mt-8 space-y-3">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Note:</strong> All plans include access to MahStry AI Tutor with
                  daily usage limits. Unused doubts do not roll over to the next day.
                  Secure payment powered by Razorpay. Cancel anytime.
                </p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                  <strong>Mahstry Private Limited</strong> • GSTIN: 08AAUCM5530E1Z9
                  <br />
                  All prices include 18% GST (CGST 9% + SGST 9%)
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AITutorSubscriptionModal;
